import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import config from '../../../constants/apiConfig';
import { selectUserGuid } from '../../../slices/commonSelectors';
import { ObjectiveBase } from '../../../types/iapBaseTypes';
import { toUtcIso } from '../../../utils/dateTime';
import { IapScope, INCIDENT } from '../ICSConstants';
import { useActualIncidentId } from './commonHooks';
import { keys as incidentStrategyKeys } from './incidentStrategyHooks';
import {
  CreateObjectiveReqData,
  keys as objectiveKeys,
  useCreateObjective,
} from './objectiveHooks';

export type IncidentObjective = ObjectiveBase & {
  id: UUID;
  objective_id: UUID;
  incident_id: UUID;
  user_guid: UUID;
  version: number;
  timestamp: Date;
  archived: boolean;
  strategies: UUID[];
  user_name: string;
};

export const keys = {
  base: 'incident_objectives',
  get all() {
    return [keys.base];
  },
  list: (incidentId: UUID, userId: UUID) => [keys.base, incidentId, userId],
} as const;

export const useIncidentObjectives = <TData = IncidentObjective[]>(
  scope: IapScope = INCIDENT,
  selector?: (i: IncidentObjective[]) => TData
) => {
  const incidentId = useActualIncidentId();
  const userGuid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, userGuid),
    queryFn: async () => {
      const resp = await axios.get<IncidentObjective[]>(
        `${config.apiGateway.ics_incident_objectives}/${incidentId}`
      );
      return resp.data.map((o) => ({
        ...o,
        timestamp: toUtcIso(o.timestamp) as IncidentObjective['timestamp'],
      }));
    },
    select: selector,
    // Guard on incidentId so callers outside an incident route (e.g. the DGPT approval modal at the hub) don't fetch
    // `/objectives/undefined`. Mirrors useIncidentRoster's `enabled: !!incidentId`.
    enabled: scope === INCIDENT && !!incidentId,
    throwOnError: (error) => {
      toast.error(error.message);
      return false;
    },
  });
};

/**
 * NOTE: this posts to the group objective which will create the incident
 * version.
 */
export const useCreateIncidentObjective = () => {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const mutation = useCreateObjective();
  return {
    ...mutation,
    mutateAsync: (data: CreateObjectiveReqData) => {
      data.incident_id = incidentId;
      return mutation.mutateAsync(data, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: objectiveKeys.all });
          queryClient.invalidateQueries({ queryKey: [keys.base] });
        },
      });
    },
  };
};

export interface EditIncidentObjectiveReqData extends Partial<ObjectiveBase> {
  objective_id: UUID;
  archived: boolean;
  strategies: UUID[];
}

export type EditResponse<A> = {
  data: A;
  meta: {
    [key: string]: boolean;
  };
};

export const useEditIncidentObjective = (objectiveId?: UUID) => {
  //N.B.: The specific edit path this uses hits an INSERT rather than an UPDATE, so the user_guid is required despite this being an edit.
  // P.M., 2025-12-15
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditIncidentObjectiveReqData) => {
      return await axios.put<EditResponse<IncidentObjective>>(
        `${config.apiGateway.ics_incident_objectives}/${incidentId}/${objectiveId ?? data.objective_id}`,
        { ...data, user_guid }
      );
    },
    onSuccess: (resp, _) => {
      queryClient.invalidateQueries({
        queryKey: keys.all,
      });
      queryClient.invalidateQueries({
        queryKey: objectiveKeys.all,
      });
      if (resp.data.meta?.updatedStrategies) {
        queryClient.invalidateQueries({
          queryKey: incidentStrategyKeys.all,
        });
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
